import * as pdfjs from "pdfjs-dist";
import { createCanvas } from "./imageEnhancer";

// Configure workerSrc using standard pdfjs worker entry or cdn fallback
try {
  if (typeof window !== "undefined") {
    // Check if worker is configured
    if (!pdfjs.GlobalWorkerOptions.workerSrc) {
      // Use CDN or reliable worker path
      pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version || "3.11.174"}/pdf.worker.min.js`;
    }
  }
} catch (e) {
  console.warn("PDF worker initialization note:", e);
}

/**
 * Load and render all pages of a PDF into high-resolution HTML5 canvases
 * @param {File|Blob|ArrayBuffer|Uint8Array} source
 * @param {Object} options { scale: 2.0, maxPages: 100 }
 * @returns {Promise<Array<{ pageNumber: number, canvas: HTMLCanvasElement, width: number, height: number }>>}
 */
export async function renderPdfToCanvases(source, options = {}) {
  const { scale = 2.0, maxPages = 100 } = options;

  let data;
  if (source instanceof ArrayBuffer) {
    data = new Uint8Array(source);
  } else if (source instanceof Uint8Array) {
    data = source;
  } else if (source instanceof Blob || source instanceof File) {
    const arrayBuffer = await source.arrayBuffer();
    data = new Uint8Array(arrayBuffer);
  } else {
    throw new Error("Invalid PDF source format");
  }

  const loadingTask = pdfjs.getDocument({
    data,
    cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version || "3.11.174"}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version || "3.11.174"}/standard_fonts/`,
  });

  const pdf = await loadingTask.promise;
  const numPages = Math.min(pdf.numPages, maxPages);
  const results = [];

  for (let p = 1; p <= numPages; p++) {
    const page = await pdf.getPage(p);
    const viewport = page.getViewport({ scale });

    const canvas = createCanvas(viewport.width, viewport.height);
    const ctx = canvas.getContext("2d");

    // Pure white background for PDF rendering
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, viewport.width, viewport.height);

    await page.render({
      canvasContext: ctx,
      viewport,
    }).promise;

    results.push({
      pageNumber: p,
      totalPages: pdf.numPages,
      width: viewport.width,
      height: viewport.height,
      canvas,
    });
  }

  return results;
}
